import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import {
  ProjectType,
  RotationOffsets,
  frameRates,
} from '@cartwhl/common/types';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';

import { AnimationMiniPlayer, MiniControls } from 'components/AnimationPlayer';
import { useTurnstile } from 'hooks/useTurnstile';
import {
  animationPlayerStore,
  characterPickerStore,
  noticesStore,
  posingStore,
  projectStore,
  sceneSettingsStore,
} from 'stores';
import { EXPORT_TYPES } from 'stores/animationGenerationStore';
import { pollProjectExportStatus } from 'stores/projectStore';
import getCharacterDataFromProject from 'utils/getCharacterDataFromProject';

import { useDownloadFreeInfo } from '../hooks';
import { exportAnimationFile } from '../utils';
import ExportLoader from './ExportLoader';
import OrientationInfo from './OrientationInfo';

export default function DownloadFile({
  tab,
  downloadFormat,
  setDownloadFormat,
  handleOpenChange,
}: {
  tab: EXPORT_TYPES;
  downloadFormat: EXPORT_TYPES;
  setDownloadFormat: (format: EXPORT_TYPES) => void;
  handleOpenChange: (open: boolean) => void;
}) {
  const {
    currentProject,
    export: projectExport,
    motion: { combinedBvhFile },
  } = useSnapshot(projectStore.state);
  const { characters } = useSnapshot(characterPickerStore.state);
  const { poseRenderInformation } = useSnapshot(posingStore.state);

  const downloadPlayerStore = animationPlayerStore.getAnimationPlayerStoreByID(
    currentProject.data?.projectID + tab,
  );

  const {
    cameraSettings: { cameraFollow, serializedCamera },
    characterSettings,
    playbackSettings: { frameRate, frameStepSize, playbackSpeed },
  } = useSnapshot(sceneSettingsStore.state);

  const characterData = getCharacterDataFromProject(currentProject?.data);

  const [fileDownloadPath, setFileDownloadPath] = useState('');
  const [keyframeSimplification, setKeyframeSimplification] = useState<
    'none' | 'reduce' | 'simplify'
  >('none');
  const [ikFeet, setIkFeet] = useState(true);
  const [ikHands, setIkHands] = useState(false);
  const [withMesh, setWithMesh] = useState(true);
  const [showTurnstileWidget, setShowTurnstileWidget] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [pendingDownload, setPendingDownload] = useState(false);

  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  const {
    render: renderTurnstile,
    remove: removeTurnstile,
    widgetId,
    error: turnstileError,
  } = useTurnstile({
    onSuccess: (receivedToken) => {
      setTurnstileToken(receivedToken);
      setShowTurnstileWidget(false);
      setPendingDownload(true);
    },
    onError: (errorCode) => {
      noticesStore.error(
        `Verification failed: ${errorCode}. Please try again.`,
      );
    },
    theme: 'auto',
  });

  const { downloadIsFree, showDownloadFreeExamples, isFreeExport } =
    useDownloadFreeInfo(downloadFormat);

  // Check if current project is a pose project
  const isPoseProject =
    currentProject.status === 'ready' &&
    currentProject.data?.type === ProjectType.POSE;

  useEffect(() => {
    setDownloadFormat(tab);
    setFileDownloadPath('');
    if (tab === EXPORT_TYPES.BVH) {
      setWithMesh(false);
      setKeyframeSimplification('none');
    } else if (tab === EXPORT_TYPES.MA || tab === EXPORT_TYPES.FBX_MAYA) {
      setWithMesh(true);
      setKeyframeSimplification('simplify');
    }
  }, [tab, setDownloadFormat]);

  useEffect(() => {
    const exportData = projectExport.data;
    if (projectExport.status === 'ready' && exportData?.path) {
      exportAnimationFile(exportData.path, 'file', currentProject.data?.projectName);
      setFileDownloadPath(exportData.path);
      projectStore.clearExportPath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectExport.status]);

  useEffect(() => {
    // Take trim values from main player
    const trimFrameStart = animationPlayerStore.state.trimFrameStart ?? 0;
    const trimFrameEnd =
      animationPlayerStore.state.trimFrameEnd ??
      animationPlayerStore.state.duration.frames;

    downloadPlayerStore.setTrimFrames([trimFrameStart, trimFrameEnd]);
  }, [downloadPlayerStore]);

  // Render Turnstile widget when it should be shown
  useEffect(() => {
    if (showTurnstileWidget && turnstileContainerRef.current && !widgetId) {
      // Only render if no widget exists yet
      renderTurnstile(turnstileContainerRef.current);
    } else if (!showTurnstileWidget && widgetId) {
      // Clean up the widget when hiding it (only if one exists)
      removeTurnstile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTurnstileWidget, widgetId]);

  // Proceed with download once we have the Turnstile token
  useEffect(() => {
    if (pendingDownload && turnstileToken) {
      proceedWithDownload(turnstileToken);
      setPendingDownload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDownload, turnstileToken]);

  // Helper function to check if format requires Turnstile
  const requiresTurnstile = (format: EXPORT_TYPES): boolean => {
    return format !== EXPORT_TYPES.MP4;
  };

  // Main download logic extracted to a function that accepts a token
  const proceedWithDownload = (token: string | null = null) => {
    // For the Nov 2024 upload launch: if the character is a custom character their .ma has no custom controls.
    const activeCharacter = characters?.find(
      (c) => c.PK === characterData?.characterID,
    );
    if (
      downloadFormat === EXPORT_TYPES.MA &&
      activeCharacter?.createdBy !== 'FREE_CHARACTERS'
    ) {
      noticesStore.info(
        "Our advanced control system is designed for specific type of character rigs. If your character isn't compatible with our rigging system, the Maya file will contain a basic rig.",
      );
    }

    if (currentProject.data) {
      projectStore.exportCurrentProject({
        format: downloadFormat,
        ikFeet,
        ikHands,
        includeMesh: withMesh,
        keyframeSimplification,
        rotationOffsets: characterData?.rotationOffsets as RotationOffsets,
        turnstileToken: token || undefined,
      });
      return;
    }
  };

  return (
    <div className="relative flex flex-col space-y-4">
      {isFreeExport && (
        <div className="bg-green/15 flex items-center gap-4 rounded-lg px-4 py-3 md:-mt-8">
          <CheckmarkIcon className="text-green" />
          <Text className="flex-1 text-center" variant="studio/body/sm">
            You can download this motion for free
          </Text>
        </div>
      )}
      {downloadIsFree && !isFreeExport && (
        <div className="bg-green/15 flex items-center gap-4 rounded-lg px-4 py-3 md:-mt-8">
          <CheckmarkIcon className="text-green" />
          <Text className="flex-1 text-center" variant="studio/body/sm">
            You already own this motion
          </Text>
        </div>
      )}
      {showDownloadFreeExamples && (
        <div className="bg-blue/10 flex items-center gap-4 rounded-lg px-4 py-3 md:-mt-8">
          <Text className="flex-1 text-center" variant="studio/body/sm">
            Download free examples and test our trial rig{' '}
            <Link
              className="text-blue underline"
              href="https://docs.getcartwheel.com/docs/integrating-cartwheel/integration"
              target="_blank"
            >
              here
            </Link>
          </Text>
        </div>
      )}

      {projectExport.status === 'pending' ||
      projectExport.status === 'ready' ? (
        <ExportLoader
          estimatedFinishedAt={
            new Date(projectExport.data?.estimatedFinishTime || '')
          }
          exportStartedAt={new Date(projectExport.data?.startedAt || '')}
          fileDownloadPath={projectExport.data?.path || ''}
          pollExportStatus={pollProjectExportStatus}
        />
      ) : showTurnstileWidget ? (
        <div className="bg-gray-15 dark:bg-gray-90 flex aspect-3/2 flex-col items-center justify-center gap-6 overflow-hidden rounded-lg px-8 py-12 sm:aspect-video">
          <Text variant="studio/body/sm" className="text-center">
            Please complete the verification below to download your file.
          </Text>

          <div
            ref={turnstileContainerRef}
            className="flex min-h-[65px] w-full items-center justify-center"
          />

          {turnstileError && (
            <div className="bg-red/10 flex items-center gap-2 rounded-lg px-4 py-2">
              <WarningIcon className="text-red" />
              <Text variant="studio/body/sm">
                Verification failed. Please try again.
              </Text>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-3/2 overflow-hidden rounded-lg sm:aspect-video">
          <AnimationMiniPlayer
            playerName="download"
            animationPath={poseRenderInformation?.bvhURL}
            animationPlayerStore={downloadPlayerStore}
            bvhData={combinedBvhFile || undefined}
            characterID={characterData?.characterID}
            meshPath={characterData?.meshPath}
            rotationOffsets={characterData?.rotationOffsets as RotationOffsets}
            cameraFollow={cameraFollow}
            characterSettings={characterSettings}
            defaultCameraPosition="close"
            forceShowSkeleton={!withMesh}
            frameRate={frameRate}
            frameStepSize={frameStepSize}
            playbackSpeed={isPoseProject ? 0 : playbackSpeed}
            serializedCamera={serializedCamera}
            disableCameraControls
            miniControls={
              <MiniControls.Root animationPlayerStore={downloadPlayerStore}>
                <MiniControls.FrameCount />
                <MiniControls.Timeline />
              </MiniControls.Root>
            }
          />
        </div>
      )}

      {tab === EXPORT_TYPES.BVH && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3">
          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Frame rate</Text>
              <Select
                inModal={true}
                value={frameRate}
                onValueChange={(value) => {
                  sceneSettingsStore.set(
                    'playbackSettings',
                    'frameRate',
                    parseInt(value),
                  );
                }}
                items={frameRates.map((frameRate) => ({
                  label: frameRate.toString(),
                  value: frameRate,
                }))}
              />
            </div>
          )}
          <OrientationInfo className="sm:col-span-3" mobileVariant="collapse" />
        </div>
      )}

      {tab === EXPORT_TYPES.FBX_MAYA && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          <div className="flex flex-col gap-3">
            <Text variant="studio/body/medium/sm">Software</Text>
            <Select
              inModal={true}
              value={downloadFormat}
              onValueChange={(value) => {
                setDownloadFormat(value as EXPORT_TYPES);
                setFileDownloadPath('');
              }}
              items={[
                {
                  label: 'Maya',
                  value: EXPORT_TYPES.FBX_MAYA,
                },
                {
                  label: 'Blender',
                  value: EXPORT_TYPES.FBX_BLENDER,
                },
                {
                  label: 'Unreal',
                  value: EXPORT_TYPES.FBX_UNREAL,
                },
                {
                  label: 'Roblox',
                  value: EXPORT_TYPES.FBX_ROBLOX,
                },
              ]}
            />
          </div>

          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Frame rate</Text>
              <Select
                inModal={true}
                value={frameRate}
                onValueChange={(value) => {
                  sceneSettingsStore.set(
                    'playbackSettings',
                    'frameRate',
                    parseInt(value),
                  );
                }}
                items={frameRates.map((frameRate) => ({
                  label: frameRate.toString(),
                  value: frameRate,
                }))}
              />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Text variant="studio/body/medium/sm">Character</Text>
            <div className="flex flex-1 items-center">
              <Checkbox
                id="with"
                checked={withMesh}
                label="Include Mesh"
                variant="checkbox"
                color="settings"
                setChecked={(checked) => {
                  setWithMesh(checked);
                  setFileDownloadPath('');
                }}
              />
            </div>
          </div>

          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Spline simplification</Text>
              <Select
                inModal={true}
                value={keyframeSimplification}
                onValueChange={(value) => {
                  setKeyframeSimplification(
                    value as 'none' | 'reduce' | 'simplify',
                  );
                }}
                items={[
                  {
                    label: 'None',
                    value: 'none',
                  },
                  {
                    label: 'Rebuild curves',
                    value: 'simplify',
                  },
                ]}
              />
            </div>
          )}
          <OrientationInfo className="col-span-2" />
        </div>
      )}

      {tab === EXPORT_TYPES.GLB && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Frame rate</Text>
              <Select
                inModal={true}
                value={frameRate}
                onValueChange={(value) => {
                  sceneSettingsStore.set(
                    'playbackSettings',
                    'frameRate',
                    parseInt(value),
                  );
                }}
                items={frameRates.map((frameRate) => ({
                  label: frameRate.toString(),
                  value: frameRate,
                }))}
              />
            </div>
          )}
          <div
            className={`flex flex-col gap-3 ${!isPoseProject ? 'pt-6' : ''}`}
          >
            <Text variant="studio/body/medium/sm">Character</Text>
            <div className="flex flex-1 items-center">
              <Checkbox
                id="with"
                checked={withMesh}
                label="Include Mesh"
                variant="checkbox"
                color="settings"
                setChecked={(checked) => {
                  setWithMesh(checked);
                  setFileDownloadPath('');
                }}
              />
            </div>
          </div>

          <OrientationInfo className="col-span-2" />
        </div>
      )}

      {tab === EXPORT_TYPES.MA && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          <div className="flex flex-col gap-3">
            <Text variant="studio/body/medium/sm">Format</Text>
            <Select
              inModal={true}
              value={downloadFormat}
              onValueChange={(value) => {
                setDownloadFormat(value as EXPORT_TYPES);
                setFileDownloadPath('');
              }}
              items={[
                {
                  label: 'Maya ASCII (.ma)',
                  value: EXPORT_TYPES.MA,
                },
                {
                  label: 'Maya Binary (.mb)',
                  value: EXPORT_TYPES.MB,
                },
              ]}
            />
          </div>

          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Frame rate</Text>
              <Select
                inModal={true}
                value={frameRate}
                onValueChange={(value) => {
                  sceneSettingsStore.set(
                    'playbackSettings',
                    'frameRate',
                    parseInt(value),
                  );
                }}
                items={frameRates.map((frameRate) => ({
                  label: frameRate.toString(),
                  value: frameRate,
                }))}
              />
            </div>
          )}

          {!isPoseProject && (
            <div className="flex flex-col gap-3">
              <Text variant="studio/body/medium/sm">Spline simplification</Text>
              <Select
                inModal={true}
                value={keyframeSimplification}
                onValueChange={(value) => {
                  setKeyframeSimplification(
                    value as 'none' | 'reduce' | 'simplify',
                  );
                }}
                items={[
                  {
                    label: 'None',
                    value: 'none',
                  },
                  // On 5/20/2025 Mitch, Neil, and Cat agreed to remove this as an option since it isn't a true redundant removal
                  // {
                  //   label: 'Remove redundant keys',
                  //   value: 'reduce',
                  // },
                  {
                    label: 'Rebuild curves',
                    value: 'simplify',
                  },
                ]}
              />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Text variant="studio/body/medium/sm">IK Settings</Text>
            <div className="flex gap-3">
              <Checkbox
                id="ik-feet"
                checked={ikFeet}
                color="settings"
                label="IK Feet"
                setChecked={setIkFeet}
                variant="checkbox"
              />
              <Checkbox
                id="ik-hands"
                checked={ikHands}
                color="settings"
                label="IK Hands"
                setChecked={setIkHands}
                variant="checkbox"
              />
            </div>
          </div>

          <OrientationInfo className="col-span-2" />
        </div>
      )}

      <div className="border-gray-30 dark:border-gray-75 dark:bg-gray-90 sticky -bottom-4 z-10 -mx-4 flex justify-between rounded-b-2xl border-t bg-white px-4 pt-4 pb-4 sm:mx-0 sm:px-0 md:pb-0">
        <Button onClick={() => handleOpenChange(false)}>Close</Button>
        <Button
          variant="primary"
          disabled={projectExport.status === 'pending' || showTurnstileWidget}
          onClick={() => {
            // Check if this format requires Turnstile verification
            if (requiresTurnstile(downloadFormat)) {
              // Show Turnstile widget in place of video player
              setShowTurnstileWidget(true);
            } else {
              // For MP4, proceed directly without Turnstile
              proceedWithDownload(null);
            }
          }}
        >
          {downloadIsFree ? 'Download (for free)' : 'Download'}
        </Button>
      </div>
    </div>
  );
}
